import React, { useEffect, useState, memo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Modal,
  ModalHeader,
  Form,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle
} from "@hello-pangea/dnd";
import Button from "@mui/material/Button";

import toPage from "../../assets/images/icons/console-control-1.svg";
import Delete from "../../assets/images/icons/whiteboard-menu-c-3.svg";
import edit from "../../assets/images/icons/whiteboard-menu-a-5.svg";
import copy from "../../assets/images/icons/whiteboard-menu-b-5-2.svg";
import board_list from "../../assets/images/tool/board_list.svg";
import drag from "../../assets/images/tool/drag-icon.svg";

import {
  getAuthToken
} from "../../utils/auth";

import {
  BoardListGetAction,
  BoardElementListGetAction,
  BoardPageCopyAction,
  BoardPageUpdateAction,
  BoardPageDeleteAction,
  BoardLastPageAction,
} from "../../store/screen/actions";
import { screenState, setError } from "../../store/screen/slice";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import { toggleFullscreen } from "../../utils/actions";

const BASE_URL = `${process.env.REACT_APP_API_URL}/elements/image/`;

interface Props {
  id: string;
  accessToken: string;
  dataList: any[];
  screenMode: any;
  setScreenMode: any;
  showSettingModal: boolean;
  toggleSettingModal: () => void;
  showPageModal: boolean;
  togglePageModal: () => void;
  setBoardSchedule: any;
  setPageUpdateLoad: any;
  pageToThis: (page: number) => void;
  setSocketReset: any;
  setChangePage: any;
  boardItem: any;
  elementListGet: any;
  toolScale: any;
}

const BoardList = ({
  id,
  accessToken,
  dataList,
  screenMode,
  setScreenMode,
  showSettingModal,
  toggleSettingModal,
  showPageModal,
  togglePageModal,
  setBoardSchedule,
  setPageUpdateLoad,
  pageToThis,
  setSocketReset,
  setChangePage,
  boardItem,
  elementListGet,

  toolScale,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorMsg, success, boards, pageInfo, boardInfo } = useAppSelector(state => screenState(state));
  const [onReady, setOnReady] = useState(false);
  const [deleteList, setDeleteList] = useState([]);
  const [deleteType, setDeleteType] = useState("page");
  const [showDeleteType, setShowDeleteType] = useState(false);
  const [showDeleteList, setShowDeleteList] = useState(false);

  const [name, setName] = useState("");
  const [bgItem, setBgItem] = useState<any>(null);
  const [editTarget, setEditTarget] = useState("main");

  const setBackground = () => {
    if (boardInfo) {
      let draw = "";
      if (boardInfo?.content) {
        draw = JSON.parse(boardInfo?.content).draw? JSON.parse(boardInfo?.content).draw : "";
      }

      dispatch(BoardPageUpdateAction({
        id: id,
        board_id: boardInfo.id,
        pageNum: pageInfo.pageNum,
        is_deleted: false,
        background_id: boardInfo.background_id,
        name: name,
        content: JSON.stringify({ draw: draw }),
        order_num: boardInfo.order_num
      }));
    }
  };

  const setBackgroundItem = () => {
    if (bgItem) {
      let draw = "";
      if (bgItem?.content) {
        draw = JSON.parse(bgItem?.content).draw? JSON.parse(bgItem?.content).draw : "";
      }

      dispatch(BoardPageUpdateAction({
        id: id,
        board_id: bgItem.id,
        pageNum: pageInfo.pageNum,
        is_deleted: false,
        background_id: bgItem.background_id,
        name: name,
        content: JSON.stringify({ draw: draw }),
        order_num: bgItem.order_num
      }));
    }
  };

  const pageCopy = (item: any) => {
    let draw = "";
    if (bgItem?.content) {
      draw = JSON.parse(bgItem?.content).draw? JSON.parse(bgItem?.content).draw : "";
    }
    dispatch(BoardPageCopyAction({
      id: id,
      board_id: item.id,
      pageNum: pageInfo.pageNum,
      is_deleted: false,
      background_id: item.background_id,
      name: name,
      content: JSON.stringify({ draw: draw }),
      order_num: item.order_num
    }));

    setSocketReset(1);  // wait for boards update done
    setChangePage(pageInfo.pageNum);
  };

  const pageDelete = (item: any, index: number, pageLength: number) => {
    const pageNum = (pageInfo.pageNum + 1) === pageLength? pageInfo.pageNum - 1 : pageInfo.pageNum;
    dispatch(BoardPageDeleteAction({
      id: id,
      board_id: item.id,
      pageNum: pageNum
    }));
    dispatch(BoardLastPageAction({ id: id, page: pageNum}));

    setSocketReset(1);  // wait for boards update done
    setChangePage(pageNum);
  };

  useEffect(() => {
    setOnReady(true);
  }, []);

  const reorder = (
    list: any[],
    startIndex: number,
    endIndex: number
  ): any[] => {
    const newOjb: any[] = [];
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    result.map((item: any, idx: any) => {
      newOjb.push(Object.assign({}, item, { order_num: idx }));
    });
    return newOjb;
  };

  const arraysAreEqual = (arr1: any, arr2: any) => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i].id !== arr2[i].id) {
        return false;
      }
    }
    return true;
  };

  const onDragEnd = (result: DropResult): void => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items: any[] = reorder(
      boards,
      result.source.index,
      result.destination.index
    );

    if (!arraysAreEqual(boards, items)) {
      setPageUpdateLoad(true);
      setBoardSchedule(items);
    }
  };

  const toggleDeleteTypeModal = () => {
    setShowDeleteType(!showDeleteType);
  };

  const toggleDeleteListModal = () => {
    setShowDeleteList(!showDeleteList);
  };

  const DeleteTypeSet = (type: string, reload: any) => {
    const API_URL = process.env.REACT_APP_API_URL;
    const apiType = type === "page"? `boardPages/${id}` : `boardElements/${boardInfo?.id}`;
    const data = {
      "is_deleted": true
    };
    axios.post(`${API_URL}/api/${apiType}`, data, {
      headers: {
        'userToken': accessToken,
        'Content-Type': 'application/json',
      }
    })
    .then(response => {
      setDeleteList(response.data.data);
      if (!reload) {
        setDeleteType(type);
        toggleDeleteTypeModal();
        toggleDeleteListModal();
      }
    })
    .catch(error => {
      console.error('error:', error);
    });
  };

  const pageRecovery = (target: any) => {
    const API_URL = process.env.REACT_APP_API_URL;
    fetch(`${API_URL}/api/updatePages/${target.id}`, {
      method:"POST",
      headers: {
        "userToken": accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        "background_id": target.background_id,
        "name": target.name as string,
        "content": target.content,
        "order_num": target.order_num,
        "is_restore": true
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.status != "SUCCESS") {
        throw data;
      } else {
        DeleteTypeSet("page", true);
        const listGet = {
          id: id,
          pageNum: pageInfo.pageNum,
          is_deleted: false
        }
        dispatch(BoardListGetAction(listGet));
      }
    })
    .catch(e => {
      console.log("error", e);

      dispatch(setError("頁面更新失敗"));
    });
  };

  const componentRecovery = (target: any) => {
    const API_URL = process.env.REACT_APP_API_URL;
    const content = JSON.parse(target.content);
    content.id = "";
    content.idx = dataList.length;
    content.zIndex = dataList.length * 2;
    fetch(`${API_URL}/api/updateElements/${target.id}`, {
      method:"POST",
      headers: {
        "userToken": accessToken,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        "type": target.type,
        "content": JSON.stringify(content),
        "is_restore": true
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.status != "SUCCESS") {
        throw data;
      } else {
        DeleteTypeSet("component", true);
        if (boardInfo) {
          elementListGet({ id: boardInfo.id, is_deleted: false });
        }
      }
    })
    .catch(e => {
      console.log("error", e);

      dispatch(setError("元件更新失敗"));
    });
  };

  const componentTitleGet = (content: any) => {
    return JSON.parse(content).listTitle;
  };

  return (
    <React.Fragment>
      <button
        style={{
          transform: `scale(${ toolScale })`,
          right: `calc(46px * ${ toolScale })`,
          bottom: `calc(70px * ${ toolScale })`,
        }}
        data-sss="sss"
        className="feature-anchor top-tool-btn boardList-btn"
        onClick={() => {
          togglePageModal();
          setEditTarget("item");
        }}
        data-tooltip-content="頁面管理"
      >
        <img src={board_list} alt="" />
      </button>
      {/* <UncontrolledDropdown className="top-tool-group settings-btn">
        <DropdownToggle tag="button" className="feature-anchor top-tool-btn" id="top-tool-dropdown" data-tooltip-content="更多設定">
          <img src={dots} alt="" />
        </DropdownToggle>
        <DropdownMenu>
          <div className="top-tool-group">
            <div className="top-tool-items">
              <DropdownItem onClick={() => {
                toggleSettingModal();
                setEditTarget("main");
                if (boardInfo?.name) {
                  setName(boardInfo?.name);
                } else {
                  setName("");
                }
              }}>
                <svg id="Raw" viewBox="0 0 256.00098 256.00098" stroke="currentColor">
                  <circle cx="128.00098" cy="128.00195" r="44" fill="none" strokeLinecap="round" strokeLinejoin="round"
                    strokeWidth="24"></circle>
                  <path fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="24"
                    d="M197.40919,80.65057a83.91174,83.91174,0,0,1,6.28744,10.91L229.6429,105.964a103.88962,103.88962,0,0,1,.03183,44.05764L203.714,164.433a83.912,83.912,0,0,1-6.30466,10.90011l.49943,29.67182a103.88959,103.88959,0,0,1-38.13912,22.05638l-25.46093-15.27692a83.91075,83.91075,0,0,1-12.5921-.01L96.26983,227.04288a103.88979,103.88979,0,0,1-38.17095-22.00126l.49974-29.68828a83.91174,83.91174,0,0,1-6.28744-10.91005L26.36491,150.0399a103.88962,103.88962,0,0,1-.03182-44.05764L52.29376,91.5709a83.9124,83.9124,0,0,1,6.30466-10.9001L58.099,50.999A103.88973,103.88973,0,0,1,96.23811,28.94259l25.46094,15.27693a83.91061,83.91061,0,0,1,12.59209.00995L159.738,28.961a103.88951,103.88951,0,0,1,38.171,22.00126Z">
                  </path>
                </svg>
                <div>頁面設定</div>
              </DropdownItem>
              <DropdownItem  onClick={() => {
                togglePageModal();
                setEditTarget("item");
              }}>
                <img src={layer} style={{ width: "19px" }} alt="" />
                <div>頁面列表</div>
              </DropdownItem>
              <DropdownItem  onClick={() => toggleDeleteTypeModal()}>
                <img src={trash} style={{ width: "19px" }} alt="" />
                <div>垃圾桶</div>
              </DropdownItem>
              <DropdownItem  onClick={() => toggleFullscreen(setScreenMode)}>
                {screenMode? (
                  <img src={fullscreen} style={{ width: "19px" }} alt="" />
                ) : (
                  <img src={fullscreenExit} style={{ width: "19px" }} alt="" />
                )}
                <div>全螢幕</div>
              </DropdownItem>
            </div>
          </div>
        </DropdownMenu>
      </UncontrolledDropdown> */}
      <Modal id="settingsModal" className="h-100 w-100 items-center justify-center sm:p-14 m-auto" isOpen={showSettingModal} toggle={toggleSettingModal} modalClassName="zoomIn" zIndex={1000120} centered>
        <Form
          onSubmit={e => {
            e.preventDefault();
            return false;
          }}
        >
          <div className="relative bg-screen text-left shadow-xl overflow-y-auto overflow-x-hidden max-h-full w-full">
            <div>
              <div className="setting-box w-100">
                <div className="basis-1/2 lg:basis-3/5 xl:basis-2/3">
                  <div className="mb-4">
                    <label htmlFor="title" className="crs-subtitle"> 頁面名稱 </label>
                  </div>
                  <input id="title" name="title" type="text" className="crs-text-input mt-2" defaultValue={name} onChange={(e: any) => setName(e.target.value)}></input>
                  <div className="mt-3">
                    <Button
                      className="rounded-pill student-btn"
                      variant="contained"
                      onClick={() => {
                        if (editTarget === "main") {
                          setBackground();
                        } else if (editTarget === "item") {
                          setBackgroundItem();
                        }
                      }}
                    >
                      儲存
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </Modal>
      <Modal id="pageModal" className={`h-100 w-100 items-center justify-center ${onReady? "layerReady" : ""}`} isOpen={showPageModal} toggle={togglePageModal} modalClassName="zoomIn" zIndex={1000120}>
        <ModalHeader toggle={togglePageModal}></ModalHeader>
        <div className="w-100 mt-1 text-center"><label htmlFor="qr-code" className="crs-subtitle"> 頁面列表 </label></div>
        <div id="pagelist" className="list-content">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(provided, snapshot): JSX.Element => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="layerList pageList"
                >
                  {boards.map((item: any, index: number) => (
                    <Draggable key={item.id} draggableId={item.id} index={index}>
                      {(provided, snapshot): JSX.Element => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          
                          className={`layerItem ${snapshot.isDragging? "isDragging" : ""} ${ item.id === boardItem.id? "pageThis" : "" }`}
                          data-index={index}
                        >
                          <div className="pageDrag">
                            <img src={drag} alt=""  {...provided.dragHandleProps} />
                          </div>
                          <div className="layerTitle">
                            <div className="pageTitle">
                              {item.name}
                            </div>
                            <div className="pageInfo">
                              <div className="pageImage">
                                <img src={BASE_URL + item.backgroundInfo.picture} alt="" />
                              </div>
                              <div className="pageDelete edit-btn screen-delete">
                                <div>
                                  <div
                                    className="relative flex h-100 w-100 flex-row items-center justify-center"
                                    onClick={() => {
                                      toggleSettingModal();
                                      setBgItem(item);
                                      setName(item.name);
                                    }}
                                  >
                                    <img src={edit} alt="" style={{ width: "32px" }} />
                                  </div>
                                </div>
                                <div>
                                  <div
                                    className="relative flex h-100 w-100 flex-row items-center justify-center"
                                    onClick={() => {
                                      pageToThis(index);
                                    }}
                                  >
                                    <img src={toPage} alt="" style={{ width: "32px" }} />
                                  </div>
                                </div>
                              </div>
                              <div className="pageDelete edit-btn screen-delete">
                                <div>
                                  <div
                                    className="relative flex h-100 w-100 flex-row items-center justify-center"
                                    onClick={() => {
                                      pageCopy(item);
                                    }}
                                  >
                                    <img src={copy} alt="" style={{ width: "32px" }} />
                                  </div>
                                </div>
                                <div className={`${ index === 0? "hideDelete" : "" }`}>
                                  <div
                                    className="relative flex h-100 w-100 flex-row items-center justify-center"
                                    onClick={() => {
                                      pageDelete(item, index, boards.length);
                                    }}
                                  >
                                    <img src={Delete} alt="" style={{ width: "32px" }} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default memo(BoardList);